/**
 * Crazy Cribbage - SettingsManager
 * Encapsulates theme management, settings persistence, age verification, and match records.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { PINTS_PRESETS_121 } from '../variants/PintsPresets.js';

function getStorage(s) {
  return s || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export const THEMES = [
  {
    id: 'passion',
    name: 'Sultry Passion',
    cloth: 'Sultry passion velvet cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
  },
  {
    id: 'tournament',
    name: 'Tournament Green',
    cloth: 'Tournament green cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="4.5"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>'
  },
  {
    id: 'night',
    name: 'Night Obsidian',
    cloth: 'Night obsidian cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>'
  },
  {
    id: 'burgundy',
    name: 'Royal Burgundy',
    cloth: 'Royal burgundy velvet',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-1h14v1z"/></svg>'
  },
  {
    id: 'sapphire',
    name: 'Ocean Sapphire',
    cloth: 'Ocean sapphire cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>'
  },
  {
    id: 'rustic',
    name: 'Rustic Walnut',
    cloth: 'Rustic walnut cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>'
  },
  {
    id: 'amethyst',
    name: 'Regal Amethyst',
    cloth: 'Regal amethyst velvet',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1L9 9l-8 3 8 3 3 8 3-8 8-3-8-3z"/></svg>'
  },
  {
    id: 'charcoal',
    name: 'Stealth Charcoal',
    cloth: 'Stealth charcoal cloth',
    icon: '<svg class="menu-orbit-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>'
  }
];

export class SettingsManager {
  constructor(options = {}) {
    this.brand = options.brand || 'crazy';
    this.storage = getStorage(options.storage);
    this.recordCache = null;
  }

  static getThemeKey(brand = 'crazy') {
    if (brand === 'clash') return 'cribbageClash.theme';
    if (brand === 'adult') return 'skunkedAndStripped.theme';
    return 'crazyCribbage.theme';
  }

  static getDefaultTheme(brand = 'crazy') {
    if (brand === 'clash') return 'rustic';
    if (brand === 'adult') return 'passion';
    return 'tournament';
  }

  static getSettingsKey(brand = 'crazy') {
    if (brand === 'clash') return 'cribbageClash.settings.v1';
    if (brand === 'pints') return 'pintsAndPegs.settings.v1';
    if (brand === 'adult') return 'skunkedAndStripped.settings.v1';
    return 'crazyCribbage.settings.v1';
  }

  static getRecordKey(brand = 'crazy') {
    if (brand === 'clash') return 'cribbageClash.records.v1';
    if (brand === 'pints') return 'pintsAndPegs.records.v1';
    if (brand === 'adult') return 'skunkedAndStripped.records.v1';
    return 'crazyCribbage.records.v1';
  }

  static isAgeVerified(brand = 'crazy', storage = null) {
    if (brand !== 'pints' && brand !== 'adult') return true;
    const s = getStorage(storage);
    if (!s) return false;
    try {
      return s.getItem('cc_age_verified_' + brand) === 'true';
    } catch (e) {
      return false;
    }
  }

  static setAgeVerified(brand = 'crazy', val = true, storage = null) {
    const s = getStorage(storage);
    if (!s) return;
    try {
      s.setItem('cc_age_verified_' + brand, val ? 'true' : 'false');
    } catch (e) {}
  }

  static saveSettingsStore(data, storage = null, brand = null, suppress = false) {
    if (suppress) return;
    brand = brand || SettingsManager.getAppBrand();
    const s = getStorage(storage);
    if (!s) return;
    try {
      s.setItem(SettingsManager.getSettingsKey(brand), JSON.stringify(data));
    } catch (e) {}
  }

  static loadSettingsStore(storage = null, brand = null, defaultAdultSettings = null, adultOptionsList = null) {
    if (storage && typeof storage === 'object' && !('getItem' in storage) && (storage.brand !== undefined || storage.storage !== undefined)) {
      brand = storage.brand;
      defaultAdultSettings = storage.defaultAdultSettings;
      adultOptionsList = storage.adultOptionsList;
      storage = storage.storage;
    }
    brand = brand || SettingsManager.getAppBrand();
    const s = getStorage(storage);
    let parsed = null;
    if (s) {
      try {
        const raw = s.getItem(SettingsManager.getSettingsKey(brand));
        if (raw) parsed = JSON.parse(raw);
      } catch (e) {}
    }
    if (parsed) {
      if (brand !== 'clash') {
        const clashKeys = [
          'optNoPassingLanes', 'noPassingLanes', 'optJump', 'jump', 'optJail', 'jail',
          'optBar', 'bar', 'optTimeout', 'timeout', 'optDetour', 'detour',
          'optRoundAbout', 'roundAbout', 'optSigns', 'signs', 'optStopSign', 'stopSign',
          'optDice', 'dice', 'optWheel', 'wheel', 'optSpinningWheel', 'spinningWheel',
          'optSkunkMarkers', 'skunkMarkers', 'noPassingLaneStarts', 'jumpStarts', 'jumpDests',
          'jailHoles', 'barHoles', 'timeoutHoles', 'detourHoles', 'roundAboutHoles',
          'signsBlocks', 'stopSignHoles', 'diceHoles', 'wheelHoles', 'spinningWheelHoles',
          'clashPreset', 'clashCustomLayoutId'
        ];
        clashKeys.forEach(k => { delete parsed[k]; });
      }
      if (brand !== 'crazy') {
        const crazyKeys = ['jokers', 'optCutBeforeDiscard', 'opt31sFor5', 'opt19sFor2', 'optRoundAroundTheWorld', 'optMultipleRunsInPegging', 'optPickYourCrib', 'optSpeedoCribbage', 'optShotgunCribbage', 'optTwistedCribbage', 'optChutesAndLadders', 'optFivesCribbage', 'optSevensAndEightsCribbage', 'optDrawCribbage', 'opt7CardCribbage', 'optProgressiveCut', 'optCanadianDoubles', 'optProgressiveSteals', 'optAllRed', 'optAllBlack', 'optAllOdd', 'optAllEven', 'optAllDifferentSuits', 'optKH', 'optQS', 'optAS', 'optSpades', 'optGuessStarter', 'optStraight', 'optFullHouse', 'optStraightFlush', 'optRoyalFlush', 'optRockCrib', 'optStinkHole', 'optFlushInPlay', 'optPegThroughZones', 'optLast5PegOut', 'optBunkers', 'optStolenCrib', 'optZeroHand', 'optZeroCrib'];
        crazyKeys.forEach(k => { delete parsed[k]; });
      }
      if (brand !== 'pints') {
        const pintsKeys = ['optYouDrink', 'optGuysDrink', 'optGalsDrink', 'optLeftDrink', 'optRightDrink', 'optEveryoneDrink', 'optEveryoneButYouDrink', 'optPickWhoDrinks'];
        pintsKeys.forEach(k => {
          delete parsed[k];
          delete parsed[k + 'Count'];
          delete parsed[k + 'Holes'];
          delete parsed[k + 'Pegs'];
        });
      }
      if (brand !== 'adult') {
        const adultKeys = ['optLoseClothing', 'optGainClothing', 'optPickWhoLosesClothing', 'optRightLosesClothing', 'optLeftLosesClothing', 'optRemoveClothingChoice', 'optTouchBreast', 'optLickBreast', 'optSuckBreast', 'optTouchGenitals', 'optLickGenitals', 'optSuckGenitals', 'optFingerGenitals', 'optGenitalIntercourse', 'optTouchAnus', 'optLickAnus', 'optFingerAnus', 'optAnalIntercourse'];
        adultKeys.forEach(k => {
          delete parsed[k];
          delete parsed[k + 'Count'];
          delete parsed[k + 'Holes'];
          delete parsed[k + 'Pegs'];
        });
      }
    }
    if (brand === 'adult') {
      const getDef = defaultAdultSettings || (typeof window !== 'undefined' && typeof window.getDefaultAdultSettings === 'function' ? window.getDefaultAdultSettings : null);
      const defaults = (typeof getDef === 'function' ? getDef() : getDef) || {};
      if (!parsed) return defaults;
      const opts = adultOptionsList || (typeof window !== 'undefined' && Array.isArray(window.ADULT_OPTIONS_LIST) ? window.ADULT_OPTIONS_LIST : []);
      const hasAnyAdultOption = opts.some(opt => parsed[opt.chkId] === true);
      if (!hasAnyAdultOption) {
        const merged = Object.assign({}, defaults, parsed);
        opts.forEach(opt => {
          merged[opt.chkId] = true;
          if (merged[opt.key + 'Count'] == null) merged[opt.key + 'Count'] = (opt.group === 'strip' ? 2 : 1);
          if (merged[opt.key + 'Pegs'] == null) merged[opt.key + 'Pegs'] = 1;
          if (!merged[opt.key + 'Holes']) merged[opt.key + 'Holes'] = [...(opt.defaultHoles || [])];
        });
        return merged;
      }
      return parsed;
    }
    if (brand === 'clash') {
      const validClashTargets = [241, 361, 481];
      if (!parsed) {
        return { target: 481 };
      }
      if (!validClashTargets.includes(Number(parsed.target))) {
        parsed.target = 481;
      }
      return parsed;
    }
    if (brand === 'pints') {
      const pintsDefaults = (typeof window !== 'undefined' && window.PINTS_PRESETS_121?.standard?.config) || PINTS_PRESETS_121?.standard?.config || {};
      if (!parsed) {
        return Object.assign({ target: 121, brand: 'pints', selected3DBoardId: 'board-14' }, pintsDefaults);
      }
      if ([241, 361, 481].includes(Number(parsed.target))) {
        parsed.target = 121;
      }
      if (parsed.selected3DBoardId === 'board-cribbage-clash' || parsed.selected3DBoardId === 'board-cribbage-clash-square') {
        parsed.selected3DBoardId = 'board-14';
      }
      parsed.brand = 'pints';
      const pintsKeys = ['optYouDrink', 'optGuysDrink', 'optGalsDrink', 'optLeftDrink', 'optRightDrink', 'optEveryoneDrink', 'optEveryoneButYouDrink', 'optPickWhoDrinks'];
      const hasAnyPintsOption = pintsKeys.some(k => parsed[k] === true);
      if (!hasAnyPintsOption) {
        Object.assign(parsed, pintsDefaults);
      }
      return parsed;
    }
    if (parsed) return parsed;
    return null;
  }

  static loadRecords(storage = null, brand = 'crazy') {
    const s = getStorage(storage);
    if (!s) return {};
    const currentKey = SettingsManager.getRecordKey(brand);
    try {
      const raw = s.getItem(currentKey);
      const data = raw ? JSON.parse(raw) : {};
      return (data && typeof data === 'object') ? data : {};
    } catch (e) {
      return {};
    }
  }

  static saveRecords(map, storage = null, brand = 'crazy') {
    const s = getStorage(storage);
    if (!s) return;
    const currentKey = SettingsManager.getRecordKey(brand);
    try {
      s.setItem(currentKey, JSON.stringify(map));
    } catch (e) {}
  }

  static getVariantRecord(key, storage = null, brand = 'crazy') {
    const map = SettingsManager.loadRecords(storage, brand);
    return map[key] || { wins: 0, losses: 0 };
  }

  static recordGameResult(winner, variantKey, variantLabel, storage = null, brand = 'crazy') {
    const map = SettingsManager.loadRecords(storage, brand);
    if (!map[variantKey]) {
      map[variantKey] = { wins: 0, losses: 0, label: variantLabel || variantKey };
    }
    if (winner === 'you') {
      map[variantKey].wins += 1;
    } else {
      map[variantKey].losses += 1;
    }
    if (variantLabel) {
      map[variantKey].label = variantLabel;
    }
    SettingsManager.saveRecords(map, storage, brand);
    return map[variantKey];
  }

  /**
   * Applies a theme to document elements.
   */
  static applyTheme(themeName, options = {}) {
    const brand = options.brand || SettingsManager.getAppBrand();
    const isClash = brand === 'clash';
    let theme = isClash ? 'rustic' : (themeName || SettingsManager.getDefaultTheme(brand));

    let cur = THEMES.find(t => t.id === theme);
    if (!cur) {
      cur = THEMES[0];
      theme = cur.id;
    }
    const currentIndex = THEMES.indexOf(cur);
    const nextTheme = THEMES[(currentIndex + 1) % THEMES.length];

    if (typeof document !== 'undefined') {
      if (document.documentElement) document.documentElement.setAttribute('data-theme', theme);
      if (document.body) document.body.setAttribute('data-theme', theme);
      const app = document.getElementById('app');
      if (app) app.setAttribute('data-theme', theme);
      const overlay = document.getElementById('overlay');
      if (overlay) overlay.setAttribute('data-theme', theme);
      const modal = document.getElementById('modal');
      if (modal) modal.setAttribute('data-theme', theme);
      const waitOverlay = document.getElementById('waitOverlay');
      if (waitOverlay) waitOverlay.setAttribute('data-theme', theme);

      const b = document.getElementById('btnTheme');
      if (b) {
        b.textContent = cur.name;
        b.title = `Theme: ${cur.name} (Click to switch to ${nextTheme.name})`;
        b.style.display = isClash ? 'none' : '';
      }

      const iconSvg = cur.icon;
      const tooltip = `Theme: ${cur.name} (Click for ${nextTheme.name})`;

      const btnMenu = document.getElementById('btnThemeMenu');
      if (btnMenu) {
        btnMenu.innerHTML = iconSvg + `
          <svg class="menu-orbit-label-svg" viewBox="-50 -50 100 100" aria-hidden="true">
            <path id="arc-label-theme" d="M -35,15 A 38,38 0 0,0 35,15" fill="none"/>
            <text class="menu-orbit-label-text"><textPath href="#arc-label-theme" startOffset="50%" text-anchor="middle">THEME</textPath></text>
          </svg>
        `;
        btnMenu.title = tooltip;
        btnMenu.setAttribute('aria-label', tooltip);
        btnMenu.style.display = isClash ? 'none' : '';
      }

      const btnHeader = document.getElementById('btnThemeHeader');
      if (btnHeader) {
        btnHeader.innerHTML = iconSvg;
        btnHeader.title = tooltip;
        btnHeader.setAttribute('aria-label', tooltip);
        btnHeader.style.display = isClash ? 'none' : '';
      }
    }

    if (options.showFeedback && typeof window !== 'undefined' && typeof window.fancyCall === 'function') {
      window.fancyCall(cur.name, 'famous', 1200);
    }

    return theme;
  }

  /**
   * Toggles to the next available theme.
   */
  static toggleTheme(currentTheme, brand = 'crazy', storage = null) {
    if (brand === 'clash') return 'rustic';
    const currentIndex = THEMES.findIndex(t => t.id === currentTheme);
    const nextIndex = (currentIndex + 1) % THEMES.length;
    const nextTheme = THEMES[nextIndex].id;
    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(SettingsManager.getThemeKey(brand), nextTheme);
      } catch (e) {}
    }
    return nextTheme;
  }

  /**
   * Sets the active card back pattern on the DOM and settings store.
   */
  static setCardBack(id, options = {}) {
    const pattern = CARD_BACKS.find(p => p.id === id) || CARD_BACKS[0];
    if (typeof document !== 'undefined') {
      const app = document.getElementById('app');
      if (app) app.setAttribute('data-card-back', pattern.id);

      const prev = document.getElementById('activePatternPreview');
      if (prev) prev.className = 'pattern-preview pattern-' + pattern.id;

      const lbl = document.getElementById('activePatternLabel');
      if (lbl) lbl.textContent = pattern.name;

      const sel = document.getElementById('optCardBack');
      if (sel && sel.value !== pattern.id) {
        sel.value = pattern.id;
      }

      const dropdown = document.getElementById('cardBackDropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.card-picker-option').forEach(opt => {
          const match = opt.getAttribute('data-value') === pattern.id;
          opt.classList.toggle('selected', match);
          opt.setAttribute('aria-selected', match ? 'true' : 'false');
        });
      }
    }

    if (options.settings) {
      options.settings.cardBack = pattern.id;
    }
    if (!options.skipSave && typeof options.onSave === 'function') {
      options.onSave(pattern.id);
    }
    return pattern.id;
  }

  /**
   * Initializes the card back picker custom dropdown with keyboard and click support.
   */
  static initCardBackPicker(options = {}) {
    if (typeof document === 'undefined') return;
    const trigger = document.getElementById('cardBackTrigger');
    const dropdown = document.getElementById('cardBackDropdown');
    const sel = document.getElementById('optCardBack');
    if (!trigger || !dropdown) return;

    dropdown.innerHTML = CARD_BACKS.map(p => `
      <div class="card-picker-option ${(options.currentCardBack || 'classic-blue') === p.id ? 'selected' : ''}" role="option" data-value="${p.id}" aria-selected="${(options.currentCardBack || 'classic-blue') === p.id}" tabindex="0">
        <span class="pattern-preview pattern-${p.id}"></span>
        <div class="option-info">
          <div class="option-title">${p.name}</div>
          <div class="option-desc">${p.desc}</div>
        </div>
        <svg class="option-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
      </div>
    `).join('');

    if (sel && sel.options.length === 0) {
      sel.innerHTML = CARD_BACKS.map(p => `<option value="${p.id}">${p.name}</option>`).join('');
    }

    function openDropdown() {
      dropdown.classList.remove('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      const selectedOpt = dropdown.querySelector('.card-picker-option.selected') || dropdown.querySelector('.card-picker-option');
      if (selectedOpt) selectedOpt.focus();
    }

    function closeDropdown(focusTrigger = false) {
      dropdown.classList.add('hidden');
      trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) trigger.focus();
    }

    trigger.onclick = (e) => {
      e.stopPropagation();
      if (dropdown.classList.contains('hidden')) {
        openDropdown();
      } else {
        closeDropdown();
      }
    };

    dropdown.onclick = (e) => {
      const opt = e.target.closest('.card-picker-option');
      if (opt) {
        const val = opt.getAttribute('data-value');
        SettingsManager.setCardBack(val, options);
        closeDropdown(true);
      }
    };

    dropdown.onkeydown = (e) => {
      const optList = Array.from(dropdown.querySelectorAll('.card-picker-option'));
      const currIndex = optList.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (currIndex + 1) % optList.length;
        optList[next].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = (currIndex - 1 + optList.length) % optList.length;
        optList[prev].focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (currIndex >= 0) {
          const val = optList[currIndex].getAttribute('data-value');
          SettingsManager.setCardBack(val, options);
          closeDropdown(true);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown(true);
      }
    };

    document.addEventListener('click', (e) => {
      if (!e.target.closest('#cardBackPicker')) {
        closeDropdown();
      }
    });

    SettingsManager.setCardBack(options.currentCardBack || 'classic-blue', { ...options, skipSave: true });
  }

  /**
   * Deconstructs match settings into variant parts.
   */
  static variantParts(obj = {}) {
    const goal = obj.goal === 'low' ? 'low' : 'high';
    const target = obj.target ? String(obj.target) : '121';
    const deal = obj.deal === 'super' ? 'super' : 'std';
    const decks = parseInt(obj.decks, 10) === 2 ? '2' : '1';
    return { goal, target, deal, decks };
  }

  /**
   * Generates a unique key string for a variant setup.
   */
  static variantKeyFrom(obj) {
    const p = SettingsManager.variantParts(obj);
    return [p.goal, p.target, p.deal, p.decks].join('|');
  }

  /**
   * Generates a readable label for a variant setup.
   */
  static variantLabelFrom(obj) {
    const p = SettingsManager.variantParts(obj);
    return (p.goal === 'low' ? 'Low ball' : 'High ball') + ' · ' + p.target + ' · ' +
      (p.deal === 'super' ? 'Super' : 'Standard') + ' · ' + p.decks + ' deck' + (p.decks === '2' ? 's' : '');
  }

  /**
   * Reads the current variant configuration from the lobby form.
   */
  static currentFormVariant(context = {}) {
    const brand = context.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : 'crazy');
    const isClash = brand === 'clash';
    if (typeof document === 'undefined') {
      return { goal: 'high', target: isClash ? 481 : 121, deal: 'std', decks: 1 };
    }
    const deal = document.getElementById('optDeal');
    const goal = document.getElementById('optGoal');
    const target = document.getElementById('optTarget');
    const decks = document.getElementById('optDecks');
    if (!deal) return { goal: 'high', target: isClash ? 481 : 121, deal: 'std', decks: 1 };
    return {
      deal: deal.value,
      goal: isClash ? 'high' : (goal ? goal.value : 'high'),
      target: parseInt(target.value, 10),
      decks: parseInt(decks.value, 10) || 1
    };
  }

  /**
   * Renders the current setup win/loss stats and game record badge in the lobby.
   */
  static renderRecordBox(context = {}) {
    if (typeof document === 'undefined') return;
    const curEl = document.getElementById('recordCurrent');
    const listEl = document.getElementById('recordList');
    if (!curEl || !listEl) return;

    const brand = context.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : 'crazy');
    const storage = getStorage(context.storage);
    const v = SettingsManager.currentFormVariant({ brand });
    const rec = SettingsManager.getVariantRecord(SettingsManager.variantKeyFrom(v), storage, brand);

    curEl.style.display = 'block';
    curEl.textContent = 'This setup — ' + rec.wins + ' win' + (rec.wins === 1 ? '' : 's') + ' · ' + rec.losses + ' loss' + (rec.losses === 1 ? '' : 'es');

    const map = SettingsManager.loadRecords(storage, brand);
    const rows = Object.keys(map).map(k => {
      const r = map[k];
      return { key: k, label: r.label || k, wins: r.wins || 0, losses: r.losses || 0 };
    }).filter(r => r.wins + r.losses > 0)
      .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses));

    listEl.innerHTML = rows.length
      ? rows.map(r => '<div>' + r.label + ' — ' + r.wins + '–' + r.losses + '</div>').join('')
      : '<div>No games recorded yet.</div>';

    const gameRec = document.getElementById('gameRecord');
    if (gameRec) {
      const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
      const activeObj = settings && settings.deal ? settings : v;
      const r = SettingsManager.getVariantRecord(SettingsManager.variantKeyFrom(activeObj), storage, brand);
      gameRec.textContent = r.wins + '–' + r.losses;
    }
  }

  /**
   * Displays the modal with full historical match records across variants.
   */
  static showRecordsModal(context = {}) {
    SettingsManager.renderRecordBox(context);
    const brand = context.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : 'crazy');
    const storage = getStorage(context.storage);
    const v = SettingsManager.currentFormVariant({ brand });
    const rec = SettingsManager.getVariantRecord(SettingsManager.variantKeyFrom(v), storage, brand);
    const currentLine = 'This setup — ' + rec.wins + ' win' + (rec.wins === 1 ? '' : 's') + ' · ' + rec.losses + ' loss' + (rec.losses === 1 ? '' : 'es');

    const map = SettingsManager.loadRecords(storage, brand);
    const rows = Object.keys(map).map(k => {
      const r = map[k];
      return { key: k, label: r.label || k, wins: r.wins || 0, losses: r.losses || 0 };
    }).filter(r => r.wins + r.losses > 0)
      .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses));

    const listItemsHtml = rows.length
      ? rows.map(r => `<div style="margin:6px 0;padding:5px 0;border-bottom:1px solid rgba(244,234,212,0.1);display:flex;justify-content:space-between;align-items:center;gap:12px;"><span>${r.label}</span><strong style="color:var(--gold,#f0c64a);white-space:nowrap;">${r.wins}–${r.losses}</strong></div>`).join('')
      : '<div style="color:#d7e8dc;font-style:italic;padding:12px 0;">No games recorded yet.</div>';

    const modalHtml = `
      <h2>Game Records</h2>
      <div style="background:rgba(0,0,0,0.35);padding:10px 14px;border-radius:8px;border:1px solid rgba(240,198,74,0.35);margin:12px 0 14px;color:var(--gold,#f0c64a);font-weight:600;font-size:0.95rem;">
        ${currentLine}
      </div>
      <div class="record-list record-list-modal" style="display:block;max-height:55vh;overflow-y:auto;text-align:left;padding:10px 14px;background:rgba(0,0,0,0.28);border-radius:8px;border:1px solid rgba(244,234,212,0.18);line-height:1.6;">
        ${listItemsHtml}
      </div>
      <div style="margin-top:16px;display:flex;justify-content:center;gap:12px;flex-wrap:wrap;">
        <button type="button" class="btn-danger" id="btnResetStats">Reset Statistics</button>
        <button type="button" class="btn-gold modal-btn-close" id="btnCloseRecords">Close</button>
      </div>
    `;

    const showModalFn = context.showModal || (typeof window !== 'undefined' && window.showModal ? window.showModal : null);
    if (typeof showModalFn === 'function') {
      showModalFn(modalHtml, true);
      if (typeof document !== 'undefined') {
        const btnReset = document.getElementById('btnResetStats');
        if (btnReset) {
          btnReset.addEventListener('click', () => {
            if (typeof resetGameStatistics === 'function') resetGameStatistics();
            else SettingsManager.resetGameStatistics(context);
          });
        }
      }
    }
  }

  /**
   * Resets all stored match statistics after confirmation.
   */
  static resetGameStatistics(context = {}) {
    const showConfirmModalFn = context.showConfirmModal || (typeof window !== 'undefined' && window.showConfirmModal ? window.showConfirmModal : null);
    const onConfirm = () => {
      const brand = context.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : 'crazy');
      const storage = getStorage(context.storage);
      SettingsManager.saveRecords({}, storage, brand);
      if (storage) {
        try {
          storage.removeItem(SettingsManager.getRecordKey(brand));
        } catch (e) {}
      }
      SettingsManager.renderRecordBox(context);
      SettingsManager.showRecordsModal(context);
    };

    if (typeof showConfirmModalFn === 'function') {
      showConfirmModalFn({
        title: 'Reset All Statistics?',
        message: 'Are you sure you want to reset all game statistics, win rates, and records? This action cannot be undone.',
        confirmText: 'Reset Statistics',
        cancelText: 'Cancel',
        danger: true,
        onConfirm
      });
    } else {
      onConfirm();
    }
  }

  /**
   * Detects the current application brand based on globals, window.location search, or path.
   */
  static getAppBrand(win = (typeof window !== 'undefined' ? window : null)) {
    if (win) {
      if (win.currentBrand) return win.currentBrand;
      if (win.currentLogoBrand) return win.currentLogoBrand;
      if (typeof win.detectBrand === 'function') {
        try { return win.detectBrand(); } catch (e) {}
      }
      try {
        if (win.location && win.location.search) {
          const params = new URLSearchParams(win.location.search);
          const b = (params.get('brand') || params.get('game') || params.get('logo') || '').toLowerCase();
          if (b === 'clash' || b === 'cribbage-clash') return 'clash';
          if (b === 'pints' || b === 'pints-and-pegs' || b === 'cribbage-pints') return 'pints';
          if (b === 'adult' || b === 'stripped' || b === 'skunked-and-stripped') return 'adult';
          if (b === 'crazy' || b === 'crazy-cribbage') return 'crazy';
        }
        const href = (win.location && win.location.href ? win.location.href : '').toLowerCase();
        if (href.includes('game-skunked-and-stripped') || href.includes('skunked-and-stripped') || href.includes('adult') || href.includes('stripped')) return 'adult';
        if (href.includes('game-pints-and-pegs') || href.includes('pints-and-pegs') || href.includes('pints')) return 'pints';
        if (href.includes('game-cribbage-clash') || href.includes('cribbage-clash') || href.includes('clash')) return 'clash';
      } catch (e) {}
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      const b = document.documentElement.getAttribute('data-brand');
      if (b) return b;
    }
    return 'crazy';
  }

  /**
   * Parses game mode string into playerCount and gameMode.
   */
  static parseGameMode(val) {
    if (val === '3_shifting') return { playerCount: 3, gameMode: 'shifting' };
    if (val === '3' || val === '3_indiv') return { playerCount: 3, gameMode: 'individual' };
    if (val === '4_part') return { playerCount: 4, gameMode: 'partnership' };
    if (val === '4' || val === '4_indiv') return { playerCount: 4, gameMode: 'individual' };
    if (val === '5' || val === '5_shifting') return { playerCount: 5, gameMode: 'shifting' };
    if (val === '6' || val === '6_teams') return { playerCount: 6, gameMode: 'teams' };
    if (val === '7' || val === '7_shifting') return { playerCount: 7, gameMode: 'shifting' };
    if (val === '8' || val === '8_teams') return { playerCount: 8, gameMode: 'teams' };
    return { playerCount: 2, gameMode: 'individual' };
  }

  /**
   * Serializes playerCount and gameMode into a compact mode identifier string.
   */
  static serializeGameMode(playerCount, gameMode) {
    if (playerCount === 3) return gameMode === 'shifting' ? '3_shifting' : '3_indiv';
    if (playerCount === 4) return gameMode === 'partnership' ? '4_part' : '4_indiv';
    if (playerCount === 5) return '5_shifting';
    if (playerCount === 6) return '6_teams';
    if (playerCount === 7) return '7_shifting';
    if (playerCount === 8) return '8_teams';
    return '2';
  }

  /**
   * Determines whether the current game settings represent human-vs-human mode.
   */
  static isHvH(settingsObj, context = {}) {
    const s = settingsObj || (typeof window !== 'undefined' ? window.settings : null);
    if (!s) return false;
    const pCount = parseInt(s.playerCount, 10) || 2;
    const getAppBrandFn = context.getAppBrand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand : SettingsManager.getAppBrand);
    const b = typeof getAppBrandFn === 'function' ? getAppBrandFn() : 'crazy';
    const gameOptionsEngine = context.GameOptionsEngine || (typeof window !== 'undefined' && window.GameOptionsEngine ? window.GameOptionsEngine : (typeof globalThis !== 'undefined' && globalThis.GameOptionsEngine ? globalThis.GameOptionsEngine : null));
    const allowCpu = gameOptionsEngine && typeof gameOptionsEngine.isCpuAllowed === 'function'
      ? gameOptionsEngine.isCpuAllowed(b)
      : !['pints', 'adult'].includes(b);
    if (!allowCpu) return pCount === 2 && s.opp !== 'online';
    return s.opp === 'human' && pCount === 2;
  }
  /**
   * Returns a fresh deep clone of the default settings object.
   * @param {string} [brand='crazy']
   * @returns {Object}
   */
  static getDefaultSettings(brand = 'crazy') {
    const s = JSON.parse(JSON.stringify(DEFAULT_SETTINGS));
    if (brand === 'clash') {
      s.target = 481;
    }
    return s;
  }
}

export const CARD_BACKS = Object.freeze([
  { id: 'classic-blue', name: 'Classic Blue', desc: 'Traditional casino crosshatch' },
  { id: 'classic-red', name: 'Classic Red', desc: 'Bicycle crimson diamond weave' },
  { id: 'emerald-diamond', name: 'Emerald Diamond', desc: 'Lush green argyle with gold flecks' },
  { id: 'royal-gold', name: 'Royal Gold', desc: 'Luxurious onyx & gold filigree' },
  { id: 'vintage-tartan', name: 'Vintage Tartan', desc: 'Scottish highland green & red plaid' },
  { id: 'honeycomb', name: 'Amber Honeycomb', desc: 'Warm geometric hexagon grid' },
  { id: 'midnight-stars', name: 'Midnight Astral', desc: 'Cosmic navy with celestial starbursts' },
  { id: 'amethyst', name: 'Amethyst Lattice', desc: 'Royal imperial purple & lavender' },
  { id: 'vegas-crimson', name: 'Vegas Crimson', desc: 'Vibrant casino scarlet repeating mesh' },
  { id: 'obsidian-carbon', name: 'Stealth Carbon', desc: 'Modern charcoal & black carbon weave' }
]);
export const BOARD_FINISHES = Object.freeze([
  { id: 'maple', name: 'Natural Maple', desc: 'Classic golden honey grain' },
  { id: 'walnut', name: 'Dark Walnut', desc: 'Deep espresso timber with rich grain' },
  { id: 'cherry', name: 'Rich Cherry', desc: 'Warm reddish mahogany lacquer' },
  { id: 'obsidian', name: 'Midnight Obsidian', desc: 'Stealth charcoal ebony with gold border' },
  { id: 'driftwood', name: 'Coastal Driftwood', desc: 'Weathered salt-air ash & gray' }
]);
SettingsManager.BOARD_FINISHES = BOARD_FINISHES;

export const PEG_SKINS = Object.freeze([
  { id: 'avatar', name: 'Custom Avatar Peg', desc: 'Display your chosen persona wrapped on your peg' },
  { id: 'brass', name: 'Polished Brass', desc: 'Authentic radiant gold luster' },
  { id: 'silver', name: 'Sterling Silver', desc: 'Sleek brushed chrome metallic' },
  { id: 'cherry', name: 'Turned Cherry', desc: 'Rich reddish-brown artisanal wood' },
  { id: 'walnut', name: 'Dark Walnut', desc: 'Deep dark roasted timber finish' },
  { id: 'glow', name: 'Radiant Glow', desc: 'Luminescent neon cyan/emerald shine' }
]);
SettingsManager.PEG_SKINS = PEG_SKINS;
SettingsManager.CARD_BACKS = CARD_BACKS;
SettingsManager.THEMES = THEMES;

export const CHECK_IDS = Object.freeze({
  jokers: 'optJokers',
  optCutBeforeDiscard: 'optCutBeforeDiscard',
  opt31sFor5: 'opt31sFor5',
  opt19sFor2: 'opt19sFor2',
  optRoundAroundTheWorld: 'optRoundAroundTheWorld',
  optPickYourCrib: 'optPickYourCrib',
  optSpeedoCribbage: 'optSpeedoCribbage',
  optShotgunCribbage: 'optShotgunCribbage',
  optFivesCribbage: 'optFivesCribbage',
  optSevensAndEightsCribbage: 'optSevensAndEightsCribbage',
  optDrawCribbage: 'optDrawCribbage',
  opt7CardCribbage: 'opt7CardCribbage',
  optProgressiveCut: 'optProgressiveCut',
  optProgressiveSteals: 'optProgressiveSteals',
  optAllRed: 'optAllRed',
  optAllBlack: 'optAllBlack',
  optAllOdd: 'optAllOdd',
  optAllEven: 'optAllEven',
  optAllDifferentSuits: 'optAllDifferentSuits',
  optKH: 'optKH',
  optQS: 'optQS',
  optAS: 'optAS',
  optStraight: 'optStraight',
  optFullHouse: 'optFullHouse',
  optStraightFlush: 'optStraightFlush',
  optRoyalFlush: 'optRoyalFlush',
  optRockCrib: 'optRockCrib',
  optStinkHole: 'optStinkHole',
  optPegThroughZones: 'optPegThroughZones',
  optLast5PegOut: 'optLast5PegOut',
  optBunkers: 'optBunkers',
  optNoPassingLanes: 'optNoPassingLanes',
  optJump: 'optJump',
  optJail: 'optJail',
  optBar: 'optBar',
  optTimeout: 'optTimeout',
  optDetour: 'optDetour',
  optRoundAbout: 'optRoundAbout',
  optSigns: 'optSigns',
  optFlushInPlay: 'optFlushInPlay',
  optStolenCrib: 'optStolenCrib',
  optTwistedCribbage: 'optTwistedCribbage',
  optChutesAndLadders: 'optChutesAndLadders',
  optSpades: 'optSpades',
  optGuessStarter: 'optGuessStarter',
  optZeroHand: 'optZeroHand',
  optZeroCrib: 'optZeroCrib',
  optCanadianDoubles: 'optCanadianDoubles',
  optMultipleRunsInPegging: 'optMultipleRunsInPegging'
});
SettingsManager.CHECK_IDS = CHECK_IDS;

export const DEFAULT_SETTINGS = Object.freeze({
  opp: 'cpu',
  playerCount: 2,
  gameMode: 'individual',
  seats: [
    { id: 'p1', name: 'You', type: 'human', personality: 'shark', team: 1, color: '#d4a017' },
    { id: 'p2', name: 'The Shark', type: 'cpu', personality: 'shark', team: 2, color: '#ef4444' },
    { id: 'p3', name: 'The Gambler', type: 'cpu', personality: 'gambler', team: 1, color: '#10b981' },
    { id: 'p4', name: 'The Turtler', type: 'cpu', personality: 'turtler', team: 2, color: '#3b82f6' },
    { id: 'p5', name: 'The Maverick', type: 'cpu', personality: 'gambler', team: 1, color: '#a855f7' },
    { id: 'p6', name: 'The Grinder', type: 'cpu', personality: 'turtler', team: 3, color: '#f97316' },
    { id: 'p7', name: 'The Barracuda', type: 'cpu', personality: 'shark', team: 3, color: '#06b6d4' },
    { id: 'p8', name: 'The High Roller', type: 'cpu', personality: 'gambler', team: 4, color: '#ec4899' }
  ],
  aiProfile: 'shark', coach: 'off', deal: 'std', dealStyle: 'alternate', showDetail: 'highlight', cardAssassin: 'no', optActionCam: 'no', jailAnimation: 'yes', barAnimation: 'yes', cannonAnimation: 'yes', jumpAnimation: 'yes', speedTrapAnimation: 'yes', stopSignAnimation: 'yes', stopAnimation: 'yes', detourAnimation: 'yes', roundAboutAnimation: 'yes', scenicRouteAnimation: 'yes', goal: 'high', target: 121, decks: 1, cardBack: 'classic-blue', boardFinish: 'maple',   seriesMode: 'bo3', boardViewMode: 'room3d-fullscreen', selected3DBoardId: 'board-14', jokers: false, optCutBeforeDiscard: false, opt31sFor5: false, opt19sFor2: false, optRoundAroundTheWorld: false, optPickYourCrib: false, optSpeedoCribbage: false, optShotgunCribbage: false, optTwistedCribbage: false, optChutesAndLadders: false, optSpades: false, optGuessStarter: false, optFivesCribbage: false, optSevensAndEightsCribbage: false, optDrawCribbage: false, opt7CardCribbage: false, optProgressiveCut: false, optProgressiveSteals: false, optPegThroughZones: false, optLast5PegOut: false, optBunkers: false, optNoPassingLanes: false, noPassingLaneCount: 2, noPassingLaneStarts: [20, 50, 80, 100], optJump: false, jumpCount: 2, jumpStarts: [15, 65, 5, 115, 25, 75, 45, 95], jumpDests: [35, 50, 45, 70, 55, 60, 80, 85], optJail: false, jailCount: 2, jailHoles: [25, 75], optBar: false, barCount: 2, barHoles: [40, 85], optTimeout: false, timeoutCount: 2, timeoutHoles: [30, 90], optDetour: false, detourCount: 2, detourHoles: [10, 60], optRoundAbout: false, roundAboutCount: 2, roundAboutHoles: [35, 70], optSigns: false, signsCount: 2, signsBlocks: [{ id: 1, start: 45, size: 2, signs: ['Plus 5', 'Roll The Dice'] }, { id: 2, start: 55, size: 4, signs: ['Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2'] }, { id: 3, start: 95, size: 2, signs: ['Undo', 'Score Plus x3'] }, { id: 4, start: 105, size: 4, signs: ['Plus 5', 'Minus 10', 'Go To Jail', 'Roll The Dice'] }], optStopSign: false, stopSignCount: 2, stopSignHoles: [35, 70, 105, 155, 205, 265, 345, 425], optSkunkMarkers: false, skunkMarkerHole: 360, doubleSkunkMarkerHole: 240, optYouDrink: false, youDrinkCount: 2, youDrinkPegs: 1, youDrinkHoles: [15, 45, 75, 105], optGuysDrink: false, guysDrinkCount: 2, guysDrinkPegs: 1, guysDrinkHoles: [25, 55, 85, 115], optGalsDrink: false, galsDrinkCount: 2, galsDrinkPegs: 1, galsDrinkHoles: [35, 65, 95, 110], optLeftDrink: false, leftDrinkCount: 2, leftDrinkPegs: 1, leftDrinkHoles: [10, 40, 70, 100], optRightDrink: false, rightDrinkCount: 2, rightDrinkPegs: 1, rightDrinkHoles: [20, 50, 80, 120], optEveryoneDrink: false, everyoneDrinkCount: 2, everyoneDrinkPegs: 1, everyoneDrinkHoles: [30, 60, 90, 5], optEveryoneButYouDrink: false, everyoneButYouDrinkCount: 2, everyoneButYouDrinkPegs: 1, everyoneButYouDrinkHoles: [20, 50, 80, 110], optPickWhoDrinks: false, pickWhoDrinksCount: 2, pickWhoDrinksPegs: 1, pickWhoDrinksHoles: [25, 55, 85, 115],
  optLoseClothing: false, loseClothingCount: 2, loseClothingPegs: 1, loseClothingHoles: [10, 70, 10, 70],
  optGainClothing: false, gainClothingCount: 2, gainClothingPegs: 1, gainClothingHoles: [20, 80, 20, 80],
  optPickWhoLosesClothing: false, pickWhoLosesClothingCount: 2, pickWhoLosesClothingPegs: 1, pickWhoLosesClothingHoles: [30, 90, 30, 90],
  optRightLosesClothing: false, rightLosesClothingCount: 2, rightLosesClothingPegs: 1, rightLosesClothingHoles: [40, 100, 40, 100],
  optLeftLosesClothing: false, leftLosesClothingCount: 2, leftLosesClothingPegs: 1, leftLosesClothingHoles: [50, 110, 50, 110],
  optRemoveClothingChoice: false, removeClothingChoiceCount: 2, removeClothingChoicePegs: 1, removeClothingChoiceHoles: [60, 120, 60, 120],
  optTouchBreast: false, touchBreastCount: 1, touchBreastPegs: 1, touchBreastHoles: [5, 45, 75, 105],
  optLickBreast: false, lickBreastCount: 1, lickBreastPegs: 1, lickBreastHoles: [15, 50, 80, 110],
  optSuckBreast: false, suckBreastCount: 1, suckBreastPegs: 1, suckBreastHoles: [25, 55, 85, 115],
  optTouchGenitals: false, touchGenitalsCount: 1, touchGenitalsPegs: 1, touchGenitalsHoles: [35, 40, 70, 100],
  optLickGenitals: false, lickGenitalsCount: 1, lickGenitalsPegs: 1, lickGenitalsHoles: [45, 60, 90, 120],
  optSuckGenitals: false, suckGenitalsCount: 1, suckGenitalsPegs: 1, suckGenitalsHoles: [55, 65, 95, 115],
  optFingerGenitals: false, fingerGenitalsCount: 1, fingerGenitalsPegs: 1, fingerGenitalsHoles: [65, 45, 75, 105],
  optGenitalIntercourse: false, genitalIntercourseCount: 1, genitalIntercoursePegs: 1, genitalIntercourseHoles: [75, 55, 85, 115],
  optTouchAnus: false, touchAnusCount: 1, touchAnusPegs: 1, touchAnusHoles: [85, 50, 80, 110],
  optLickAnus: false, lickAnusCount: 1, lickAnusPegs: 1, lickAnusHoles: [95, 55, 85, 115],
  optFingerAnus: false, fingerAnusCount: 1, fingerAnusPegs: 1, fingerAnusHoles: [105, 60, 90, 120],
  optAnalIntercourse: false, analIntercourseCount: 1, analIntercoursePegs: 1, analIntercourseHoles: [115, 65, 95, 115],
  optStolenCrib: false, optFlushInPlay: false, optMultipleRunsInPegging: false, optAllRed: false, optAllBlack: false, optAllOdd: false, optAllEven: false, optAllDifferentSuits: false, optKH: false, optQS: false, optAS: false, optStraight: false, optFullHouse: false, optStraightFlush: false, optRoyalFlush: false, optRockCrib: false, optStinkHole: false, optZeroHand: false, optZeroCrib: false, optCanadianDoubles: false
});
SettingsManager.DEFAULT_SETTINGS = DEFAULT_SETTINGS;


